import { ChevronDown } from "baseui/icon";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";

import { SettingsIconButton } from "../button/SettingsIconButton";
import { CheckSvg } from "../svg/CheckSvg";
import { CloseSvg } from "../svg/CloseSvg";
import { DEFAULT_COLORS, getContrastColor } from "./utils";

type Props = {
  value: string;
  onChange: (value: string) => void;
  preview: string;
};

export const ColorPicker: React.FC<Props> = ({ value, onChange, preview }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [newColorValue, setNewColorValue] = useState(value);

  const textClass =
    getContrastColor(newColorValue) === "white" ? "text-white" : "text-black";

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={"relative"} ref={wrapperRef}>
      <div
        className={"flex cursor-pointer items-center"}
        onClick={() => setIsOpen((prev) => !prev)}>
        <div style={{ background: value }} className={"h-6 w-6 rounded-full"} />
        <div
          className={classNames(
            "flex h-6 w-6 items-center justify-center text-[#3F5266] transition-transform duration-300",
            isOpen && "rotate-180",
          )}>
          <ChevronDown size={24} />
        </div>
      </div>
      {isOpen && (
        <div
          className={
            "absolute left-[calc(100%+4px)] top-[-48px] z-[4] max-w-[350px] cursor-default select-none rounded-lg border border-[#C8D2DE] bg-white py-3"
          }>
          <div className={"mb-2 flex items-center justify-between px-3"}>
            <div className={"text-sm font-semibold text-[#1D2C3B]"}>COLORS</div>
            <div className={"flex items-center gap-2"}>
              <SettingsIconButton
                onClick={() => {
                  onChange(newColorValue);
                  setIsOpen(false);
                }}
                colorVariant={"purple"}
                size={"small"}
                icon={<CheckSvg />}
              />
              <SettingsIconButton
                size={"small"}
                onClick={() => setIsOpen(false)}
                icon={<CloseSvg />}
              />
            </div>
          </div>
          <div className={"mb-2 flex gap-1 px-3"}>
            {DEFAULT_COLORS.map((color) => (
              <div
                key={color}
                className={classNames(
                  "h-8 w-8 rounded-full border p-0.5",
                  newColorValue === color ? "border-[#6938EF]" : "border-white",
                )}>
                <div
                  key={color}
                  style={{ background: color }}
                  onClick={() => setNewColorValue(color)}
                  className={"h-full w-full cursor-pointer rounded-full"}
                />
              </div>
            ))}
          </div>
          <div
            className={
              "grid grid-cols-[max-content_1fr] items-center gap-2 border-t border-[#C8D2DE] px-3 pt-3 text-sm text-[#3F5266]"
            }>
            <span>Preview</span>
            <span
              style={{ background: newColorValue }}
              className={classNames(
                "w-fit min-w-[24px] max-w-full truncate rounded-full px-2 py-1 text-xs",
                textClass,
              )}>
              {preview}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
